import { Link } from '@tanstack/react-router';
import { ArrowUpRight, Clapperboard, Monitor } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Status, Empty } from '../components/common';
import { useLab } from '../lib/service';
import { canEdit } from '@drama/domain';
import { label, activeStatuses } from '../shared';

/** Executor status and the queue of production commands. */
export function Production({ onError }: { onError: (e: unknown) => void }) {
  const lab = useLab();
  return (
    <>
      <div className="page-heading">
        <div>
          <span className="eyebrow">THE FLOOR</span>
          <h1>
            Production queue<span>.</span>
          </h1>
          <p>A clear view of what’s happening, and what’s next.</p>
        </div>
        <Status
          value={
            lab.home?.workerOnline
              ? 'ONLINE'
              : lab.home?.executorTransport === 'trigger'
                ? 'ON_DEMAND'
                : 'WAITING_WORKER'
          }
        />
      </div>
      <div className="notice">
        <Monitor size={20} />
        <span>
          {lab.isDemo
            ? 'This workspace simulates production. No local worker or API credentials are needed.'
            : lab.home?.executorTransport === 'trigger'
              ? 'The coordinator automatically starts a Trigger.dev production session when work is queued. Completed work stays saved between sessions.'
              : 'Run the local executor to process queued jobs. If it disconnects, completed work stays saved and pending production waits.'}
        </span>
      </div>
      <div className="paper-panel">
        {lab.home?.commands.length ? (
          lab.home.commands
            .slice()
            .reverse()
            .map((c) => (
              <div className="history-row" key={c._id}>
                <Clapperboard size={19} />
                <div>
                  <strong>{label(c.kind)}</strong>
                  <Link to="/series/$projectId" params={{ projectId: c.projectId }}>
                    {lab.home?.projects.find((p) => p._id === c.projectId)?.title}
                    <ArrowUpRight size={12} />
                  </Link>
                </div>
                <Status value={c.status} />
                {activeStatuses.includes(c.status) && canEdit(lab.home!.access.role) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void lab.cancel(c._id).catch(onError)}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            ))
        ) : (
          <Empty
            title="A quiet moment in the studio"
            text="Production requests will appear here. Start by developing a series."
          />
        )}
      </div>
    </>
  );
}
